import { type ReactNode, useCallback, useEffect, useMemo } from "react";

import { LoadingSpinner } from "../../../../../components/ui/loadingSpinner/loadingSpinner";
import { cn } from "../../../../../lib/utils";
import { useAudit } from "../../../../../contexts/auditContext";
import {
  useAnalyticsStepCards,
  type StepCardDocuments,
} from "../../../../../hooks/audit/useAnalyticsStepCards";
import { useAnalyticsWorkflowVersionLink } from "../../../../../hooks/audit/useAnalyticsWorkflowVersionLink";
import { useCellSupersession } from "../../../../../hooks/audit/useCellSupersession";
import { useProcedureSessionDocuments } from "../../../../../hooks/audit/useProcedureSessionDocuments";
import {
  type AnalyticsOutputFiles,
  useAnalyticsOutputFiles,
} from "../../../../../hooks/audit/useAnalyticsOutputFiles";
import {
  useAnalyticsFileParameters,
  useAnalyticsInputBindings,
  useAnalyticsRunManifest,
  useAnalyticsStatus,
} from "../../../../../hooks/useAnalyticsWorkflows";
import { AnalyticsWorkflowError } from "../../../../../lib/analyticsWorkflow";
import { type AnalyticsColumnResult } from "../../../../../models/audit/analyticsColumnResult";
import {
  analyticsConclusionProse,
  analyticsVerdictText,
} from "../../../../../models/audit/analyticsTranslator";
import { type Citation } from "../../../../../models/audit/citation";
import {
  type AuditSession,
  type ProcedureColumn,
} from "../../../../../models/audit/auditSession";
import {
  stoppedAtStepName,
  verdictRunDate,
} from "../../lib/deterministicSteps";
import { AnalyticsNeedsSetupBanner } from "../AnalyticsNeedsSetupBanner";
import { ConclusionSection } from "./ConclusionSection";
import {
  servedDeletedWorkflowRecordedAgainst,
  servedDeletedWorkflowReviewCopy,
} from "./deletedWorkflowCopy";
import { InputParametersSection } from "./InputParametersSection";
import { KeyWorkflowStepsSection } from "./KeyWorkflowStepsSection";
import { AnalyticsOutputViewerDialog } from "./AnalyticsOutputViewerDialog";
import { OutputFilesSection } from "./OutputFilesSection";
import { PaneSection } from "./PaneSection";
import { WorkflowSection } from "./WorkflowSection";
import { useSessionAnalyticsOwner } from "../../../../../hooks/audit/useSessionAnalyticsOwner";

/**
 * Whether the key-steps section reports that the run's cards could not be read.
 *
 * Taken from the resolver rather than from the step log behind it: a log this
 * build cannot read leaves the empty set a run nobody starred also leaves, and
 * only one of the two means there is nothing to show. Answered arm by arm, so a
 * state added to the union has to choose between those two readings here
 * rather than take the silent one.
 */
function keyStepsUnreadable(cardDocuments: StepCardDocuments): boolean {
  switch (cardDocuments.kind) {
    case "unreadable":
      return true;
    case "read":
    case "noRun":
      return false;
    default: {
      const unhandled: never = cardDocuments;
      return unhandled;
    }
  }
}

/**
 * The notice both of a superseded cell's surfaces show in place of its
 * evidence. One sentence, exported so the fold-out's steps tab states the same
 * fact in the same words rather than inventing a second wording for it.
 */
export const NEWER_RESULT_NOTICE =
  "This cell now holds a newer result. Reopen the cell to see it.";

const DIVIDED_BLOCK_CLASS =
  "-mx-4 space-y-2 border-b border-stone-200 px-4 pb-4";

/**
 * A section whose reads have not answered yet.
 *
 * The heading stays and the body waits. Both of the sentences these sections
 * can otherwise say are claims — that the workflow declares no file parameters,
 * or that the run's files could not be read — and a request still in the air,
 * or one not yet sent, supports neither. The spinner is the same one the panels
 * around it wait with.
 */
function PendingPaneSection({
  title,
  label,
}: {
  title: string;
  label: string;
}) {
  return (
    <PaneSection title={title}>
      <div className="flex justify-center py-4">
        <LoadingSpinner label={label} />
      </div>
    </PaneSection>
  );
}

interface AnalyticsReviewSectionsProps {
  auditSession: AuditSession;
  /** The verdict the sections report on, already resolved by the caller. */
  analyticsResult: AnalyticsColumnResult;
  /** The procedure column the verdict was reached on. */
  column: ProcedureColumn;
  sourceRowId?: string;
  /**
   * Rendered last, inside the group `workflow_deleted` removes.
   *
   * A slot rather than a section of its own: the log is the verdict's action
   * history, and the surface that owns the write appending to it is the surface
   * that must be able to refetch it.
   */
  activityLog?: ReactNode;
  /**
   * Rendered under the conclusion; in a superseded cell, above the newer-result
   * notice rather than under it.
   *
   * It says who concluded the held verdict, whose outcome badge the host still
   * shows once the cell has moved on, so it stays to keep that badge attributed
   * — and it goes above the notice because under it the line would read as the
   * account of the result the notice announces.
   */
  provenance?: ReactNode;
  /**
   * The step whose card the host's viewer pane is showing, where it has one.
   *
   * The three below are one optional group, and they are optional because the
   * two surfaces rendering this review differ in what sits beside it: the
   * fold-out has a viewer pane and can honour a selection, the sheet has none.
   * Passed through untouched — the selection is the host's state, and a copy
   * held here would be a second answer to which step is open.
   */
  selectedStepId?: string | null;
  onSelectStep?: (stepId: string) => void;
  onToggleStar?: (citation: Citation) => void;
  /**
   * The output files' view and download state, where the host shows an opened
   * file itself. The fold-out passes it to open files in its viewer pane; the
   * sheet has no pane, so without it the review opens them in its own dialog.
   */
  outputFiles?: AnalyticsOutputFiles;
}

/**
 * The review of an analytics verdict: `CONCLUSION`, then one block holding
 * everything Optro Analytics produced — `OUTPUT FILES`, `KEY WORKFLOW STEPS`,
 * `WORKFLOW`, `INPUT PARAMETERS`, in that order — closed by a line saying
 * Optro Analytics manages it.
 *
 * One definition, rendered by both surfaces that show this review: the sheet an
 * analytics cell opens without the auditor-trail flag, and the fold-out's
 * `Workpaper Review` tab. A second copy is two accounts of one run free to
 * drift apart while each claims to be the run's own.
 *
 * Every read the sections need is issued here, so they stay presentational and
 * one component owns what is in flight. Each stays disabled until its ids are
 * known.
 *
 * Each read is carried in the states its section speaks to, because the
 * sections below say something different in each. A failure keeps the heading
 * and states itself, wherever a section would otherwise make a claim about the
 * run on the strength of its empty fallback; a read still in flight is carried
 * the same way in reverse — it has not failed, and the failure line is as much
 * a claim as the empty one.
 *
 * The one thing the result cannot report is whether the read went out at all: a
 * disabled query answers with no data, not loading and no error, byte-identical
 * to one whose first fetch is still open, so `issued` reports it instead. INPUT
 * PARAMETERS waits on a not-yet-issued read rather than vanish — its vanishing
 * is the claim that the workflow declares no file parameters, which a read that
 * never went out has not established, and its reads become issued once the
 * control id lands and the column's status has answered. OUTPUT FILES does not
 * wait on its disabled read, because the manifest is also disabled on a
 * selection that names no row, which no waiting resolves; a never-issued
 * manifest is left to the nothing-to-show rule, and a manifest 404 joins it
 * there — the ordinary "this cell has no recorded run" is an answer of nothing,
 * not a failure.
 *
 * **A section with nothing to show is absent**, heading and all. Each section
 * decides that for itself, because what counts as nothing differs between them.
 *
 * **Where the cell has moved on to a later verdict, the evidence sections give
 * way to one notice** and the log, which is the column's history rather than
 * this run's evidence, stays. So does the provenance line, set above the
 * notice: it names who concluded the held verdict, not what any run found. The
 * verdict is the caller's and the run beside it is read live for the cell, so
 * the two are only one account of one run for as long as the cell still holds
 * this verdict; past that the sections would state a conclusion over another
 * run's evidence, and one sentence below is composed from both. It is the
 * sections that give way and not the pane: the hosts mount the override dialog
 * beside this review precisely so a re-read cannot take an auditor's half-typed
 * conclusion down with it, and a notice that unmounted further up would do
 * exactly that.
 *
 * **In the `workflow_deleted` state the evidence sections are removed rather
 * than emptied, and what remains says why.** The conclusion and its figures are
 * Midship's own and still stand; the step detail, the input parameters and the
 * output files are gone with the workflow, and a heading over an empty
 * container would report evidence that is merely missing. A paragraph under the
 * banner states that, and the WORKFLOW section stays as the record of what
 * produced the result — unlinked, because there is nothing left to open. The
 * workflow is named by id and never by name — Midship stores no name, and the
 * live lookup that would supply one is the call answering `410 Gone` in that
 * very state — and it is named once below the banner rather than by both.
 */
export function AnalyticsReviewSections({
  auditSession,
  analyticsResult,
  column,
  sourceRowId,
  activityLog,
  provenance,
  selectedStepId,
  onSelectStep,
  onToggleStar,
  outputFiles: hostOutputFiles,
}: AnalyticsReviewSectionsProps) {
  const { controlSession, sessionKind } = useAudit();
  const owner = useSessionAnalyticsOwner();
  const analyticsColumnId = analyticsResult.analyticsColumnId;

  const {
    data: status,
    error: statusError,
    isLoading: statusPending,
  } = useAnalyticsStatus(owner, analyticsColumnId);
  const {
    data: runManifestRead,
    error: runManifestError,
    isLoading: runManifestLoading,
  } = useAnalyticsRunManifest(owner, analyticsColumnId, sourceRowId);
  const workflowDeleted = status?.status === "workflow_deleted";
  // A workflow's fate is not yet known while the first status read is in
  // flight, and the live branch's sections are exactly what a deletion removes
  // or unlinks — so drawing them now offers rows that are about to vanish, each
  // an action against a workflow that may be gone: a step selected from one
  // opens a card whose picture outlives the workflow it describes, an output
  // file's download answers `410 Gone`, and the WORKFLOW link offers a version
  // of a workflow that may no longer exist. Every live-branch section that
  // offers an action against the workflow waits on it; the run manifest is
  // still read meanwhile, because the deleted branch draws its WORKFLOW from
  // it. Asked only of the first read: `isLoading` rises again on every refetch
  // while the answer stays cached, and holding on that would pull the sections
  // out from under an auditor each time one fires. A failed read releases the
  // hold, so an outage there does not also keep every section waiting.
  const workflowFateUnsettled = status === undefined && statusPending;
  // Held until the status answers and withheld where it says deleted: there the
  // read answers `workflowDeleted` with no parameters, and the deleted branch
  // draws no INPUT PARAMETERS, so it is a round trip for nothing. A status
  // behind the deletion still issues it, and `inputsUnreadable` honours the
  // flag. A failed status read releases it, so an outage there does not also
  // cost the section its rows.
  const {
    data: fileParameters,
    error: fileParametersError,
    isLoading: fileParametersLoading,
    issued: fileParametersIssued,
  } = useAnalyticsFileParameters(owner, analyticsColumnId, {
    enabled: !workflowDeleted && !workflowFateUnsettled,
  });
  const {
    data: inputBindings,
    error: inputBindingsError,
    isLoading: inputBindingsLoading,
    issued: inputBindingsIssued,
  } = useAnalyticsInputBindings(owner, analyticsColumnId);
  const {
    data: sessionDocuments,
    error: sessionDocumentsError,
    isLoading: sessionDocumentsLoading,
  } = useProcedureSessionDocuments(auditSession.id);
  // Resolved from the document table the session arrived with rather than the
  // documents read above, which hides the agent's cards, so the cards have no
  // read of their own to wait on.
  const { resolved, cardDocuments } = useAnalyticsStepCards(
    auditSession.documents,
    analyticsResult,
  );
  const mintWorkflowVersionLink = useAnalyticsWorkflowVersionLink(owner);

  // Addressed by the cell, so the link opens the version that produced this
  // result rather than whichever version Optro Analytics has activated now:
  // the route reads the run its manifest names. Minted on the press and never
  // rendered, because what it answers carries a Cascade session credential.
  const openWorkflowVersion = useCallback(
    () =>
      sourceRowId
        ? mintWorkflowVersionLink(analyticsColumnId, sourceRowId)
        : Promise.resolve(null),
    [mintWorkflowVersionLink, analyticsColumnId, sourceRowId],
  );

  const ownOutputFiles = useAnalyticsOutputFiles(
    owner,
    analyticsColumnId,
    sourceRowId,
  );
  const outputFiles = hostOutputFiles ?? ownOutputFiles;

  const runManifest = analyticsResult.runManifest;
  const workflowId =
    column.analyticsColumn?.workflowBinding?.automationWorkflowId ?? null;

  // Where the cell has moved on to a later verdict the evidence sections give
  // way to one notice, and the provenance line and the log stay. Read from the
  // shared reader rather than derived here, so this review and the fold-out's
  // steps tab — the other surface that frames this verdict over live reads —
  // cannot drift into two shapes of the same comparison.
  const supersededByNewerResult = useCellSupersession({
    procedureSessionId: auditSession.id,
    sourceRowId,
    heldVerdict: analyticsResult,
  });

  // The newer-result notice and the workflow-deleted state both replace the
  // live sections, and the dialog with them, so an open viewer is unmounted
  // with its open flag still set. Closing clears that flag and abandons a view
  // still being minted, so the dialog must not come back open, over the file
  // it last showed, when the sections return.
  const { closeViewer: closeOwnViewer } = ownOutputFiles;
  useEffect(() => {
    if (supersededByNewerResult || workflowDeleted) {
      closeOwnViewer();
    }
  }, [supersededByNewerResult, workflowDeleted, closeOwnViewer]);

  // The one list of published files, read from the route that projects them. A
  // failed read leaves it undefined rather than empty, which is what separates
  // "the run published nothing" from "this build did not learn what it
  // published" — the conclusion's callout below would otherwise say the first
  // on the strength of the second.
  const publishedOutputs =
    runManifestError === null ? runManifestRead?.outputs : undefined;
  const publishedNothing =
    publishedOutputs !== undefined && publishedOutputs.length === 0;

  // A 404 on the manifest route is the ordinary "this cell has no recorded run",
  // documented as such where the hook is defined — an answer of nothing, not a
  // failure to read it. Every other error is a real failure to read.
  const runManifestNotFound =
    runManifestError instanceof AnalyticsWorkflowError &&
    runManifestError.kind === "not_found";

  // Absent rather than failed on two counts that are not failures: the manifest
  // read is disabled without a control id and on a selection that names no row —
  // a request that never went out — and a 404 is an answer of nothing, not an
  // unreadable one. Both are left to the nothing-to-show rule; only a real
  // failure to read keeps the heading with the failure stated.
  const outputsUnreadable = runManifestError !== null && !runManifestNotFound;

  // Whether any parameter is filled by a named document. The other two binding
  // classes state the rule that finds their file — whichever file the test table
  // marks, or the sample's own row — and name no document, so a parameter set
  // without one of these is complete with no filenames at all and must not be
  // held behind a read it does not draw from. Read off the bindings themselves,
  // which the section is already pending on while that read is in flight.
  const bindsADocument = Object.values(inputBindings?.bindings ?? {}).some(
    (binding) => binding.source === "document",
  );

  // INPUT PARAMETERS is drawn from three reads — the declarations make the rows,
  // the bindings fill them, and the session's documents supply the filename a
  // document binding points at. Never asked and still waiting both wait here:
  // the declarations and bindings reads are disabled without a control id, the
  // declarations also until the status answers, a disabled read is
  // byte-identical to one in flight, and neither has established the section's
  // absence-claim that the workflow declares no file parameters. The documents
  // read is always issued — its session id is always known — so only its
  // in-flight state waits, and only when a document binding needs the filename
  // it supplies.
  const inputsPending =
    !fileParametersIssued ||
    !inputBindingsIssued ||
    fileParametersLoading ||
    inputBindingsLoading ||
    (bindsADocument && sessionDocumentsLoading);

  // Any of the three failing costs the section its truthfulness: without the
  // declarations there are no rows, without the bindings every row it draws
  // reads as an unfilled slot, and without the documents a parameter bound to a
  // specific file is described as filled by another file. That last is the same
  // false claim about the run's inputs by a different route, so it takes the
  // same exit rather than a new one.
  //
  // A deleted workflow answers with an empty list that is not an absence. The
  // status this read is gated on can be behind the deletion, so the flag is
  // honoured here rather than letting the empty list claim the workflow
  // declares no file parameters.
  const inputsUnreadable =
    fileParametersError !== null ||
    fileParameters?.workflowDeleted === true ||
    inputBindingsError !== null ||
    (bindsADocument && sessionDocumentsError !== null);

  // The step a run that published nothing stopped at. Both halves of the claim
  // are read from the source that owns them: the stopping point from the
  // verdict's step log through the one function that reads a step's completion
  // against the run's terminality, and the empty publication from the manifest
  // route.
  const failedStepName = publishedNothing
    ? stoppedAtStepName(runManifest)
    : null;

  const conclusionProse = analyticsConclusionProse(analyticsResult);

  // An inconclusive verdict's outcome text can describe a pass that was
  // withdrawn, so it is shown only when the verdict has no account of the gap.
  // Where the verdict text is already the prose — an inconclusive verdict's
  // account of the gap, or a judged verdict whose trail line states the outcome
  // — the figures line is left out rather than repeat it.
  const verdictText = analyticsVerdictText(analyticsResult);
  const conclusionFigures =
    verdictText !== conclusionProse ? verdictText : null;

  // Rebuilt only when the documents themselves change: this is the whole
  // procedure session's document list, and the renders that do not touch it —
  // a download setting its own state, say — would otherwise walk all of it
  // again for a Map indistinguishable from the last.
  const documentNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const sessionDocument of sessionDocuments?.documents ?? []) {
      const fileName =
        sessionDocument.fetchableDocument?.fileName ?? sessionDocument.name;
      if (fileName !== null) {
        names.set(sessionDocument.documentId, fileName);
      }
    }
    return names;
  }, [sessionDocuments?.documents]);

  if (supersededByNewerResult) {
    return (
      <>
        {provenance}

        <PaneSection title="NEWER RESULT">
          <p className="text-sm text-font-secondary">{NEWER_RESULT_NOTICE}</p>
        </PaneSection>

        {/* Kept where the evidence sections are not: the log is the column's
            action history rather than this run's evidence, so it pairs nothing
            — and it is where an auditor just told their cell moved on can read
            who moved it. */}
        {activityLog}
      </>
    );
  }

  return (
    <>
      {/* The rules under this block and the Optro Analytics one are pulled out
          by the fold-out body's padding so they span the pane; the sheet pads
          wider, so there they stop just short of its edges. */}
      <div className={DIVIDED_BLOCK_CLASS}>
        <ConclusionSection
          prose={conclusionProse}
          figures={conclusionFigures}
          mitigatingFactors={analyticsResult.potentialMitigatingFactors}
          performanceIssues={
            analyticsResult.isConclusive
              ? []
              : analyticsResult.performanceIssues
          }
          failedStepName={failedStepName}
        />
        {provenance}
      </div>

      {statusError !== null ? (
        <AnalyticsNeedsSetupBanner unreadable />
      ) : (
        status && (
          <AnalyticsNeedsSetupBanner
            status={status.status}
            statusCopy={status.statusCopy}
            invalidOutput={status.invalidOutput}
            workflowId={
              // Withheld in the one state the banner names it. The paragraph
              // directly below names the same id in its first sentence, and
              // one id said twice in adjacent lines reads as two workflows.
              workflowDeleted ? null : workflowId
            }
            builderUrl={status.builderUrl}
            session={
              controlSession
                ? { id: controlSession.id, kind: sessionKind }
                : null
            }
          />
        )
      )}

      {workflowDeleted ? (
        <>
          <p className="text-sm whitespace-pre-wrap break-words">
            {servedDeletedWorkflowReviewCopy(
              status?.deletedWorkflowCopy?.review,
              workflowId,
              { warnOnMissing: !statusPending },
            )}
          </p>

          {/* The record of what produced the result, kept where the evidence
              sections are not: the conclusion above would otherwise stand with
              nothing saying what ran it. Its figures are the verdict's own
              manifest, which survives the workflow it names.

              The provenance line is the run's, so it comes off the manifest
              payload rather than the column's status: the status reports the
              column's most recent run, and reading it here would date this
              pane by a run it is not describing. The manifest serves that line
              for a live workflow too, so it is read only in this branch.
              Waited on only for the first read, as the status is. */}
          {runManifestRead === undefined && runManifestLoading ? (
            <PendingPaneSection title="WORKFLOW" label="Loading workflow" />
          ) : (
            <WorkflowSection
              deleted
              workflowId={workflowId}
              version={runManifest?.automationWorkflowVersionLabel ?? null}
              recordedAgainst={servedDeletedWorkflowRecordedAgainst(
                runManifestRead?.deletedWorkflowCopy?.recordedAgainst,
              )}
            />
          )}
        </>
      ) : (
        <>
          <div className={cn(DIVIDED_BLOCK_CLASS, "space-y-6")}>
            {runManifestLoading || workflowFateUnsettled ? (
              <PendingPaneSection
                title="OUTPUT FILES"
                label="Loading output files"
              />
            ) : (
              <OutputFilesSection
                outputs={publishedOutputs ?? []}
                unreadable={outputsUnreadable}
                onView={outputFiles.view}
                onDownload={outputFiles.download}
                pending={outputFiles.pendingFileIds}
                // Read off the open flag, because the viewed file outlives the
                // close: the dialog draws it through its exit animation, and a
                // step picked over the pane closes it without dropping it.
                selectedFileId={
                  outputFiles.viewerOpen
                    ? (outputFiles.viewed?.output.fileId ?? null)
                    : null
                }
                // The same test the handlers make, so the button an auditor
                // can press is exactly the one the handlers will honour.
                fetchable={!!sourceRowId}
              />
            )}

            {workflowFateUnsettled ? (
              <PendingPaneSection
                title="KEY WORKFLOW STEPS"
                label="Loading key workflow steps"
              />
            ) : (
              <KeyWorkflowStepsSection
                resolved={resolved}
                unreadable={keyStepsUnreadable(cardDocuments)}
                runManifest={runManifest}
                selectedStepId={selectedStepId}
                onSelectStep={onSelectStep}
                onToggleStar={onToggleStar}
              />
            )}

            {workflowFateUnsettled ? (
              <PendingPaneSection title="WORKFLOW" label="Loading workflow" />
            ) : (
              <WorkflowSection
                name={status?.workflowName ?? null}
                version={runManifest?.automationWorkflowVersionLabel ?? null}
                runDate={verdictRunDate(runManifest)}
                onOpen={openWorkflowVersion}
              />
            )}

            {inputsPending ? (
              <PendingPaneSection
                title="INPUT PARAMETERS"
                label="Loading input parameters"
              />
            ) : (
              <InputParametersSection
                parameters={fileParameters?.parameters ?? []}
                bindings={inputBindings?.bindings ?? {}}
                unreadable={inputsUnreadable}
                documentNames={documentNames}
              />
            )}

            <p className="text-xs text-font-secondary">
              Managed by Optro Analytics
            </p>
          </div>

          {activityLog}

          {!hostOutputFiles && (
            <AnalyticsOutputViewerDialog
              open={ownOutputFiles.viewerOpen}
              viewed={ownOutputFiles.viewed}
              onClose={ownOutputFiles.closeViewer}
              onClosed={ownOutputFiles.viewerClosed}
              onDownload={ownOutputFiles.download}
              pendingFileIds={ownOutputFiles.pendingFileIds}
            />
          )}
        </>
      )}
    </>
  );
}
